/**
 * Sentry — graceful no-op when REACT_APP_SENTRY_DSN is empty.
 *
 * Imported first in index.js so it instruments everything, including
 * React's own initialization.
 *
 * Security posture:
 *   - No PII by default
 *   - Session replay masks ALL text + blocks ALL media (we don't want
 *     screenshots of encrypted messages ending up in Sentry)
 *   - Never captures localStorage keys prefixed `ghostcomm_` (those
 *     hold the user's trust ledger + panic-button config)
 *   - Filters 401/403/404/429 before-send
 *   - Drops events triggered by browser extensions
 */
import * as Sentry from '@sentry/react';

const DSN = process.env.REACT_APP_SENTRY_DSN;

// Always safe to call these even when Sentry didn't init.
export let sentryEnabled = false;

export function initSentry() {
  if (!DSN) return false;

  Sentry.init({
    dsn: DSN,
    environment: process.env.REACT_APP_ENV || 'development',
    release: process.env.REACT_APP_VERSION || 'government-shield-1.0',

    // Privacy posture
    sendDefaultPii: false,
    tracesSampleRate: parseFloat(
      process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '0.1'
    ),
    replaysSessionSampleRate: 0, // never record healthy sessions
    replaysOnErrorSampleRate: parseFloat(
      process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '0.1'
    ),

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],

    beforeSend(event, hint) {
      // Filter out browser-extension noise.
      const frames = event?.exception?.values?.[0]?.stacktrace?.frames || [];
      if (
        frames.some(
          (f) =>
            f.filename &&
            (f.filename.includes('chrome-extension://') ||
              f.filename.includes('moz-extension://') ||
              f.filename.includes('safari-web-extension://'))
        )
      ) {
        return null;
      }

      // AbortError from cancelled fetches = expected UX, not a bug.
      const err = hint?.originalException;
      if (err && err.name === 'AbortError') return null;

      return event;
    },

    beforeBreadcrumb(breadcrumb) {
      // Strip Authorization headers + ghostcomm_ localStorage keys
      // from breadcrumbs so they never get uploaded.
      if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
        const headers = breadcrumb.data?.request_headers;
        if (headers && typeof headers === 'object') {
          ['authorization', 'Authorization', 'cookie', 'Cookie'].forEach((k) => {
            if (headers[k]) headers[k] = '[Filtered]';
          });
        }
      }
      if (
        breadcrumb.category === 'console' &&
        typeof breadcrumb.message === 'string' &&
        breadcrumb.message.includes('ghostcomm_')
      ) {
        return null;
      }
      return breadcrumb;
    },

    ignoreErrors: [
      // Expected browser noise
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      // Our own auth 401s are re-raised as HTTP errors by axios — drop them
      'Request failed with status code 401',
      'Request failed with status code 403',
      'Request failed with status code 404',
      'Request failed with status code 429',
    ],
  });

  sentryEnabled = true;
  return true;
}

// Re-export so other modules can call it without another import
export const SentryErrorBoundary = Sentry.ErrorBoundary;
export const captureException = Sentry.captureException;
